import { VERSION, COMMIT } from './version'

/**
 * The debug log.
 *
 * A ring buffer of what this browser has been doing, readable in the app by an
 * admin and by nobody else.
 *
 * It exists because the hardest bugs on these projects arrive as "it does not
 * work on my phone" from someone on an island, with no console to open, no
 * cable to plug in, and a device you will never hold. `client_errors` catches
 * what THREW; this catches the twenty seconds before it, which is usually where
 * the answer is.
 *
 * ── What it does NOT do ─────────────────────────────────────────────────────
 *
 * It never leaves the device on its own. Nothing is uploaded, nothing is
 * persisted; closing the tab loses it. Sending a log to someone is an explicit
 * act by the person holding the phone, via Copy.
 *
 * It never records request or response BODIES — only method, path and status.
 * A body is where a session token, a PIN or somebody's phone number lives, and
 * a debug log that captures those is a credential store with a Copy button.
 */

export type Level = 'log' | 'warn' | 'error' | 'net' | 'app'

export interface Entry {
  at: number
  level: Level
  message: string
}

/**
 * 400 entries.
 *
 * Enough to hold the run-up to a failure, small enough that a page in a render
 * loop cannot turn the buffer into a memory leak. The oldest go first: what
 * happened most recently is what is being asked about.
 */
const MAX = 400
const buffer: Entry[] = []
let installed = false
const listeners = new Set<() => void>()

function push(level: Level, message: string): void {
  buffer.push({ at: Date.now(), level, message: message.slice(0, 400) })
  if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX)
  for (const l of listeners) l()
}

function stringify(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a
      if (a instanceof Error) return `${a.name}: ${a.message}`
      try {
        return JSON.stringify(a)
      } catch {
        // A circular object is still worth knowing the shape of.
        return String(a)
      }
    })
    .join(' ')
}

/**
 * Tee `console` into the buffer, WITHOUT replacing it.
 *
 * The original is always called first. A logging helper that swallows console
 * output makes every future debugging session harder than the one it was added
 * to help with.
 */
export function installDebugLog(): void {
  if (installed) return
  installed = true

  for (const level of ['log', 'warn', 'error'] as const) {
    const original = console[level].bind(console)
    console[level] = (...args: unknown[]) => {
      original(...args)
      push(level, stringify(args))
    }
  }

  push('app', `${VERSION} ${COMMIT} · ${navigator.language} · ${screen.width}×${screen.height}`)
}

/** Called by the API client. Method, path and status — never a body. */
export function logRequest(method: string, path: string, status: number, ms: number): void {
  push('net', `${method} ${path} → ${status} (${Math.round(ms)}ms)`)
}

/** For the app to mark something worth seeing in the run-up. */
export function note(message: string): void {
  push('app', message)
}

export function entries(): readonly Entry[] {
  return buffer
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function clear(): void {
  buffer.length = 0
  for (const l of listeners) l()
}

/** What someone pastes into a message. Build identity first — it is question one. */
export function asText(): string {
  const head = [
    `version  ${VERSION}`,
    `commit   ${COMMIT}`,
    `agent    ${navigator.userAgent}`,
    `when     ${new Date().toISOString()}`,
    '',
  ].join('\n')
  const body = buffer
    .map((e) => `${new Date(e.at).toISOString().slice(11, 23)} ${e.level.padEnd(5)} ${e.message}`)
    .join('\n')
  return `${head}${body}`
}
